import { Badge, Button } from '@fsd/ui';
import { ArrowRight, CheckCircle2, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
// P60 images
import p60_1 from '@/assets/drones/p60/1.webp';
import p60_2 from '@/assets/drones/p60/2.webp';
import p60_3 from '@/assets/drones/p60/3.webp';
import p60_4 from '@/assets/drones/p60/4.webp';
import p60_5 from '@/assets/drones/p60/5.webp';
// P100 Pro images
import p100pro1 from '@/assets/drones/p100pro/1.webp';
import p100pro2 from '@/assets/drones/p100pro/2.webp';
import p100pro3 from '@/assets/drones/p100pro/3.webp';
import p100pro4 from '@/assets/drones/p100pro/4.webp';
import p100pro5 from '@/assets/drones/p100pro/5.webp';
// P150 images
import p150_1 from '@/assets/drones/p150/1.webp';
import p150_2 from '@/assets/drones/p150/2.webp';
import p150_3 from '@/assets/drones/p150/3.webp';
import p150_4 from '@/assets/drones/p150/4.webp';
import p150_5 from '@/assets/drones/p150/5.webp';
import { useTranslation } from '@/i18n/hooks';
import { scrollToSection } from '@/utils';

const allImages = [
  // P100 Pro
  p100pro1,
  p100pro2,
  p100pro3,
  p100pro4,
  p100pro5,
  // P150
  p150_1,
  p150_2,
  p150_3,
  p150_4,
  p150_5,
  // P60
  p60_1,
  p60_2,
  p60_3,
  p60_4,
  p60_5,
];

// Função para embaralhar array (Fisher-Yates shuffle)
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i];
    if (temp !== undefined && shuffled[j] !== undefined) {
      shuffled[i] = shuffled[j] as T;
      shuffled[j] = temp as T;
    }
  }
  return shuffled;
}

const heroImages = shuffleArray(allImages);
const IMAGE_CHANGE_INTERVAL = 4000; // 4 segundos

export function Hero() {
  const { t } = useTranslation('home');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, IMAGE_CHANGE_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleIndicatorClick = (index: number) => {
    setCurrentImageIndex(index);
    // Reset interval when user manually changes image
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, IMAGE_CHANGE_INTERVAL);
  };

  return (
    <section
      id="start"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-linear-to-br from-slate-50 via-white to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900"
    >
      {/* Background decorative elements - reduced on mobile */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 sm:-top-40 sm:-right-40 w-40 h-40 sm:w-80 sm:h-80 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-20 -left-20 sm:-bottom-40 sm:-left-40 w-40 h-40 sm:w-80 sm:h-80 bg-primary/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 lg:py-32">
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
          {/* Text Content - Left Side */}
          <div className="text-center lg:text-left space-y-4 sm:space-y-6 lg:space-y-8 animate-fade-in-up">
            {/* Badge */}
            <Badge
              variant="primary"
              size="lg"
              icon={<CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />}
              className="animate-fade-in"
            >
              {t('hero.badge')}
            </Badge>

            {/* Main Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900 dark:text-slate-100 leading-tight">
              <span className="block animate-slide-in-left">{t('hero.title.line1')}</span>
              <span className="block text-primary animate-slide-in-left delay-200">
                {t('hero.title.line2')}
              </span>
            </h1>

            {/* Description */}
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto lg:mx-0 animate-fade-in delay-300 px-2 sm:px-0">
              {t('hero.description')}
            </p>

            {/* Features List */}
            <ul className="flex flex-col gap-3 sm:gap-4 justify-center lg:justify-start animate-fade-in delay-500">
              {[
                { key: 'feature1', text: t('hero.features.feature1') },
                { key: 'feature2', text: t('hero.features.feature2') },
                { key: 'feature3', text: t('hero.features.feature3') },
              ].map((feature) => (
                <li
                  key={feature.key}
                  className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-slate-700 dark:text-slate-300 justify-center lg:justify-start"
                >
                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start pt-2 sm:pt-4 animate-fade-in delay-700">
              <Button
                size="lg"
                onClick={() => scrollToSection('#new-drones')}
                className="group w-full sm:w-auto"
              >
                {t('hero.cta.primary')}
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => scrollToSection('#contact')}
                className="w-full sm:w-auto"
              >
                {t('hero.cta.secondary')}
              </Button>
            </div>
          </div>

          {/* Image - Right Side - Hidden on mobile, shown from tablet */}
          <div className="hidden md:block relative lg:order-last animate-fade-in-right">
            {/* Image Container with decorative elements */}
            <div className="relative">
              {/* Decorative gradient circle behind image */}
              <div className="absolute inset-0 bg-linear-to-br from-primary/20 to-primary/5 rounded-3xl blur-2xl transform rotate-6 scale-110" />

              {/* Main Image */}
              <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                {heroImages.map((image, index) => (
                  <img
                    key={`hero-image-${image}`}
                    src={image}
                    alt={`${t('hero.image.alt')} ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                ))}
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-linear-to-t from-slate-900/20 to-transparent z-10" />

                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                  {heroImages.map((image, index) => (
                    <button
                      key={`hero-indicator-${image}`}
                      type="button"
                      onClick={() => handleIndicatorClick(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex
                          ? 'w-8 bg-white'
                          : 'w-2 bg-white/50 hover:bg-white/75'
                      }`}
                      aria-label={`Ir para imagem ${index + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Floating decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/20 rounded-full blur-xl animate-float" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/10 rounded-full blur-xl animate-float delay-1000" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator - adjusted for mobile */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20 pointer-events-auto">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const target = document.getElementById('new-drones');
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          className="flex flex-col items-center gap-1 sm:gap-2 text-slate-400 dark:text-slate-600 hover:text-primary dark:hover:text-primary active:text-primary dark:active:text-primary transition-colors cursor-pointer group relative z-20 pointer-events-auto touch-manipulation"
          aria-label="Scroll down"
        >
          <span className="text-xs sm:text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
            Scroll
          </span>
          <ChevronDown className="h-5 w-5 sm:h-6 sm:w-6 animate-bounce" />
        </button>
      </div>
    </section>
  );
}
